/**
 * Panel nesting onto factory paper.
 *
 * Bounding-box rectangle packing (MaxRects) of panel pieces onto sheets of
 * `paper_width` × `paper_length` (inches, from the factory's production_nesting
 * setting). 90° rotation is the packer's only real degree of freedom; 180° is
 * bbox-neutral and only recorded as an allowed orientation for directional
 * fabric. Output is one SVG per sheet at true physical size.
 *
 * Sheets embed each piece's isolated artwork once (namespaced <g> in defs)
 * and window it per placement with a plain transform group (never a nested
 * <svg> viewport — CorelDRAW drops those), so a sheet with 40 placements
 * doesn't carry 40 copies of the design. Isolated pieces are not clipped at
 * all: their ink-tight window already contains exactly their artwork, and
 * CorelDRAW ignores clipPaths. Only windowed-fallback pieces get a rect clip.
 */
import { MaxRectsPacker } from 'maxrects-packer';
import { flattenPathData, multiplyMat, unionPathData, IDENTITY, type Mat } from './clipFlatten';
import { buildDxf, type DxfPolyline } from './dxf';
import { sizeLabelPathMarkup, type GlyphFont } from './labelGlyphs';
import { edgeLabelForOutline, outlineAreaPt2 } from './labelPath';
import { namespaceSvgIds } from './logoVectorize';
import { offsetPathData } from './offset';
import { inchToPt, mmToPt, ptToInch, PT_PER_MM } from './units';
import { sizeLabelMarkup, type PanelWindow } from './panelSplitter';

export interface NestPieceInput {
  /** Unique per placement (row/panel/copy). */
  id: string;
  rowIndex: number;
  size: string;
  panelName: string;
  widthPt: number;
  heightPt: number;
  /** ViewBox window over this piece's content. */
  window: PanelWindow;
  /** Key into SheetBuildContext.contents for this piece's isolated artwork. */
  contentKey: string;
  /**
   * Size-label baseline in WINDOW coordinates (ink-anchored so the label sits
   * on the fabric, not in the bbox's empty corners). Falls back to the
   * window's bottom-center when unset.
   */
  labelAnchor?: { x: number; y: number };
  /**
   * Emit a rect clipPath around this placement. Only windowed-fallback pieces
   * need it (their content is the whole composite); isolated pieces carry
   * nothing outside their ink-tight window, and CorelDRAW's SVG import drops
   * clipPaths anyway — so clip only where skipping it would be worse.
   */
  needsClip?: boolean;
  /**
   * Cut silhouette in piece-local pt (union of the panel's fabric paths).
   * Present → the sheet draws laser-cutting outlines around it and packing
   * reserves their physical extent. Absent (fallback pieces, silhouette
   * failure) → no outlines, plain box packing.
   */
  outlineD?: string | null;
  /**
   * Optional extra label content (order no / player / panel, per factory
   * settings), joined onto the size's single label line.
   */
  labelExtra?: string | null;
  /**
   * Fabric area of the piece in pt² (from its silhouette). Drives the sheet
   * utilization figure; without it the bounding box is used, which flatters a
   * C-shaped collar into "88%" while most of its box is air.
   */
  areaPt2?: number | null;
  /**
   * Which garment of the row this piece belongs to (1-based copy number).
   * Per-row packing fills sheets whole garment by whole garment.
   */
  copyIndex?: number | null;
}

/** Fabric areas for every piece with a silhouette (keyed by piece id). */
export async function computePieceAreas(
  pieces: Array<Pick<NestPieceInput, 'id' | 'outlineD'>>
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  const memo = new Map<string, number | null>();
  for (const piece of pieces) {
    if (!piece.outlineD) continue;
    let area = memo.get(piece.outlineD);
    if (area === undefined) {
      area = await outlineAreaPt2(piece.outlineD);
      memo.set(piece.outlineD, area);
    }
    if (area) out.set(piece.id, area);
  }
  return out;
}

/** Factory nesting settings. Paper dimensions and piece padding are INCHES. */
export interface NestSettings {
  paper_width: number;
  paper_length: number;
  allowed_rotations: number[];
  /** Gap between pieces (and sheet border), inches. Falls back to the default. */
  piece_padding?: number | null;
  /** Width of the cut outline band going OUTWARDS from the panel edge (mm)
   *  (Silverstar: 4pt = 1.41mm; Rajco: 0.25mm). Always drawn. */
  cut_outline_width_mm?: number | null;
  /** Draw the offset "searching" outline around every panel. */
  search_outline?: boolean | null;
  /** How far outside the panel the searching line's centerline runs (mm). */
  search_outline_offset_mm?: number | null;
  /** Thickness of the searching line (mm). */
  search_outline_width_mm?: number | null;
}

export interface NestPlacement {
  piece: NestPieceInput;
  xPt: number;
  yPt: number;
  rotated: boolean;
}

export interface NestSheet {
  placements: NestPlacement[];
  /** Σ piece area / sheet area, 0..1. */
  utilization: number;
  /**
   * Set when packing per roster row: the row this sheet belongs to (e.g.
   * "row 3 · XL · Hansen 11"). Sheets of one row never carry another row's
   * pieces; a row that does not fit on one sheet continues on the next.
   */
  group?: string | null;
  /** Garment numbers (copyIndex) present on the sheet, when known. */
  garments?: number[];
}

export interface PackOptions {
  /** One sheet set per roster row instead of the whole order together. */
  perRow?: boolean;
  /** rowIndex -> human label for the sheet group (falls back to the size). */
  rowLabels?: Map<number, string>;
}

export interface NestResult {
  sheets: NestSheet[];
  oversized: NestPieceInput[];
  paperWidthPt: number;
  paperLengthPt: number;
  paddingPt: number;
}

export const DEFAULT_PIECE_PADDING_IN = 0.125;

/** Fixed clear margin between the sheet's bounding box and the pieces —
 *  0.25cm per ops, independent of the piece padding. */
export const SHEET_EDGE_MARGIN_MM = 2.5;

/** Size-label styling: prints the row size ON each piece, its baseline
 *  sitting exactly ON the panel's geometric bottom edge (margin 0 — glyph
 *  bottoms touch the fabric edge). 17pt = 0.6cm em box, so no glyph can
 *  exceed the required 0.6cm maximum height. */
export const SIZE_LABEL_FONT_PT = 17;
export const SIZE_LABEL_MARGIN_PT = 0;
export const SIZE_LABEL_OUTLINE_PT = 0.5;

/** Laser-cutting outlines, per factory ops specs (Silverstar/Rajco):
 *  - CUT outline: a band of configurable width going OUTWARDS from the panel
 *    edge (never over the artwork). Drawn as a 2×width stroke centered on
 *    the silhouette UNDER the artwork, whose fill covers the inward half.
 *    (Silverstar 4pt = 1.41mm, Rajco 0.25mm.) Always drawn.
 *  - SEARCHING outline (optional): a line of configurable thickness whose
 *    centerline runs a configurable distance outside the panel (Rajco: 1mm
 *    line offset 5mm out). Rendered as a big centered stroke UNDER the
 *    artwork with a white mask stroke clearing everything inside the line —
 *    equivalent to a true offset curve without offset geometry. */
export const CUT_OUTLINE_WIDTH_MM_DEFAULT = 1;
export const SEARCH_OUTLINE_OFFSET_MM_DEFAULT = 5;
export const SEARCH_OUTLINE_WIDTH_MM_DEFAULT = 1;

/** Paper bounding box drawn on every sheet: 0.25mm red outline, no fill. */
export const SHEET_FRAME_WIDTH_MM = 0.25;
export const SHEET_FRAME_COLOR = '#ff0000';

/** The frame as a PATH (Corel mangles <rect> in some transform contexts),
 *  inset by half the stroke so the full line width stays on the paper. */
function sheetFrameMarkup(paperWidthPt: number, paperLengthPt: number): string {
  const sw = mmToPt(SHEET_FRAME_WIDTH_MM);
  const i = sw / 2;
  const w = paperWidthPt - sw;
  const l = paperLengthPt - sw;
  return (
    `<path d="M${round(i)} ${round(i)}h${round(w)}v${round(l)}h${round(-w)}z"` +
    ` fill="none" stroke="${SHEET_FRAME_COLOR}" stroke-width="${round(sw)}"/>`
  );
}

export interface OutlineSpec {
  cutWidthMm: number;
  searchEnabled: boolean;
  searchOffsetMm: number;
  searchWidthMm: number;
}

export function resolveOutlineSpec(
  settings: Partial<NestSettings> | null | undefined
): OutlineSpec {
  return {
    cutWidthMm: settings?.cut_outline_width_mm ?? CUT_OUTLINE_WIDTH_MM_DEFAULT,
    searchEnabled: !!settings?.search_outline,
    searchOffsetMm: settings?.search_outline_offset_mm ?? SEARCH_OUTLINE_OFFSET_MM_DEFAULT,
    searchWidthMm: settings?.search_outline_width_mm ?? SEARCH_OUTLINE_WIDTH_MM_DEFAULT,
  };
}

/**
 * How far the outermost outline reaches past the piece box, in points.
 * Packing distances are measured FROM THIS EDGE — the configured padding is
 * always clear paper between outermost outlines.
 */
export function outlineExtentPt(
  piece: Pick<NestPieceInput, 'outlineD'>,
  spec: OutlineSpec
): number {
  if (!piece.outlineD) return 0;
  // The cut band goes fully outwards, so it reaches its whole width.
  const cutReach = spec.cutWidthMm;
  const searchReach = spec.searchEnabled ? spec.searchOffsetMm + spec.searchWidthMm / 2 : 0;
  return mmToPt(Math.max(cutReach, searchReach));
}

export function pieceFitsUpright(
  piece: Pick<NestPieceInput, 'widthPt' | 'heightPt'>,
  usableWidthPt: number,
  usableLengthPt: number
): boolean {
  return piece.widthPt <= usableWidthPt && piece.heightPt <= usableLengthPt;
}

export function pieceFitsPaper(
  piece: Pick<NestPieceInput, 'widthPt' | 'heightPt'>,
  usableWidthPt: number,
  usableLengthPt: number,
  allow90: boolean
): boolean {
  if (pieceFitsUpright(piece, usableWidthPt, usableLengthPt)) return true;
  if (!allow90) return false;
  return piece.heightPt <= usableWidthPt && piece.widthPt <= usableLengthPt;
}

interface PreparedPiece {
  piece: NestPieceInput;
  /** Piece only fits the paper turned 90° — its dims are swapped before packing. */
  preRotated: boolean;
  /** Piece box inflated by its laser-outline extent — what actually packs. */
  boxed: { widthPt: number; heightPt: number };
}

export function packPieces(
  pieces: NestPieceInput[],
  settings: NestSettings,
  paddingIn?: number,
  options: PackOptions = {}
): NestResult {
  if (!options.perRow) return packGroup(pieces, settings, paddingIn);

  // Rows in order of first appearance; each packed on its own, sheets
  // concatenated so numbering runs on across rows.
  const byRow = new Map<number, NestPieceInput[]>();
  for (const piece of pieces) {
    const list = byRow.get(piece.rowIndex);
    if (list) list.push(piece);
    else byRow.set(piece.rowIndex, [piece]);
  }
  let merged: NestResult | null = null;
  for (const [rowIndex, rowPieces] of byRow) {
    // Per row the sheets are filled garment by garment (whole garments,
    // in order) — a factory wants a sheet to be "garments 1–4", not ten
    // collars.
    const result = packGroup(rowPieces, settings, paddingIn, true);
    const label =
      options.rowLabels?.get(rowIndex) ??
      `row ${rowIndex + 1}${rowPieces[0]?.size ? ` · ${rowPieces[0].size}` : ''}`;
    const sheets = result.sheets.map((sheet) => ({ ...sheet, group: label }));
    merged = merged
      ? {
          ...merged,
          sheets: [...merged.sheets, ...sheets],
          oversized: [...merged.oversized, ...result.oversized],
        }
      : { ...result, sheets };
  }
  return merged ?? packGroup([], settings, paddingIn);
}

/** "1–4" or "1, 3, 7" for a sheet's garment numbers. */
export function garmentRange(garments: number[]): string {
  const g = [...garments].sort((a, b) => a - b);
  if (!g.length) return '';
  const contiguous = g.every((v, i) => i === 0 || v === g[i - 1] + 1);
  return contiguous && g.length > 1 ? `${g[0]}–${g[g.length - 1]}` : g.join(', ');
}

/** Filename-safe sheet stem: sheet_NN[_group]. */
export function sheetFileStem(index: number, sheet: Pick<NestSheet, 'group'>): string {
  const nn = String(index + 1).padStart(2, '0');
  const group = (sheet.group ?? '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return group ? `sheet_${nn}_${group}` : `sheet_${nn}`;
}

function packGroup(
  pieces: NestPieceInput[],
  settings: NestSettings,
  paddingIn?: number,
  garmentSets = false
): NestResult {
  // maxrects-packer mis-bins on raw floating-point inputs (fuzzed: 145/300
  // two-piece runs spilled to a second sheet purely from decimal tails in
  // border/dims; quantized to 3 decimals: 0/300). A thousandth of a point is
  // a micrometre — physically nothing — so everything fed to the packer is
  // quantized.
  const q = (v: number) => Math.round(v * 1000) / 1000;

  const effectivePaddingIn = paddingIn ?? settings.piece_padding ?? DEFAULT_PIECE_PADDING_IN;
  const paddingPt = q(inchToPt(effectivePaddingIn));
  const allow90 = settings.allowed_rotations?.includes(90) ?? false;
  const outlineSpec = resolveOutlineSpec(settings);

  // The packer keeps a fixed `border` margin free along the sheet edges
  // (0.25cm — NOT the piece padding), so that is the usable area a piece
  // must fit into.
  const borderPt = q(mmToPt(SHEET_EDGE_MARGIN_MM));

  // Packing distances are measured from the OUTERMOST laser-cutting outline:
  // the packed box is the piece plus its outline extent per side.
  const boxedOf = (piece: NestPieceInput) => {
    const extent = outlineExtentPt(piece, outlineSpec);
    return { widthPt: piece.widthPt + 2 * extent, heightPt: piece.heightPt + 2 * extent };
  };

  const paperWidthPt = q(inchToPt(settings.paper_width));
  const paperLengthPt = q(inchToPt(settings.paper_length));
  const usableWidthPt = paperWidthPt - 2 * borderPt;
  const usableLengthPt = paperLengthPt - 2 * borderPt;

  const oversized: NestPieceInput[] = [];
  const prepared: PreparedPiece[] = [];
  for (const piece of pieces) {
    const boxed = boxedOf(piece);
    if (!pieceFitsPaper(boxed, usableWidthPt, usableLengthPt, allow90)) {
      oversized.push(piece);
      continue;
    }
    // maxrects-packer does NOT rotate a rect that only fits rotated — it flags
    // it oversized. Swap the dims ourselves for rotation-only fits; the packer
    // may still rotate freely-fitting pieces on its own (rect.rot).
    const preRotated = !pieceFitsUpright(boxed, usableWidthPt, usableLengthPt);
    prepared.push({ piece, preRotated, boxed });
  }

  const newPacker = () =>
    new MaxRectsPacker(paperWidthPt, paperLengthPt, paddingPt, {
      smart: true,
      pot: false,
      square: false,
      allowRotation: allow90,
      border: borderPt,
    } as never);
  const rectOf = (entry: PreparedPiece) => ({
    width: q(entry.preRotated ? entry.boxed.heightPt : entry.boxed.widthPt),
    height: q(entry.preRotated ? entry.boxed.widthPt : entry.boxed.heightPt),
    data: entry,
  });

  const sheetArea = paperWidthPt * paperLengthPt;
  const binToSheet = (bin: any): NestSheet | null => {
    const placements: NestPlacement[] = [];
    for (const rect of bin.rects as any[]) {
      const entry = rect.data as PreparedPiece;
      if (rect.oversized) {
        // Defensive: the packer parks anything it can't place in an
        // "oversized" bin rect instead of throwing.
        oversized.push(entry.piece);
        continue;
      }
      placements.push({
        piece: entry.piece,
        xPt: rect.x,
        yPt: rect.y,
        // Drawn rotated when exactly one of pre-rotation / packer rotation applies.
        rotated: entry.preRotated !== Boolean(rect.rot),
      });
    }
    if (!placements.length) return null;
    // Fabric area when the piece knows it, else its box.
    const usedArea = placements.reduce(
      (sum, p) => sum + (p.piece.areaPt2 ?? p.piece.widthPt * p.piece.heightPt),
      0
    );
    const garments = [
      ...new Set(
        placements
          .map((p) => p.piece.copyIndex)
          .filter((c): c is number => c !== null && c !== undefined)
      ),
    ].sort((a, b) => a - b);
    return {
      placements,
      utilization: sheetArea > 0 ? usedArea / sheetArea : 0,
      garments: garments.length ? garments : undefined,
    };
  };

  const sheets: NestSheet[] = [];

  if (!garmentSets) {
    const packer = newPacker();
    packer.addArray(prepared.map(rectOf) as never);
    for (const bin of packer.bins) {
      const sheet = binToSheet(bin);
      if (sheet) sheets.push(sheet);
    }
    return { sheets, oversized, paperWidthPt, paperLengthPt, paddingPt };
  }

  // GARMENT SETS: sheets hold whole garments, in garment order. A sheet is
  // filled with complete garments as long as the next one still fits whole;
  // otherwise it is closed and the garment opens the next sheet. Only a
  // garment that does not fit on an empty sheet by itself is split.
  const byGarment = new Map<number, PreparedPiece[]>();
  for (const entry of prepared) {
    const key = entry.piece.copyIndex ?? 0;
    const list = byGarment.get(key);
    if (list) list.push(entry);
    else byGarment.set(key, [entry]);
  }
  const garments = [...byGarment.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, list]) =>
      list.sort((a, b) => b.boxed.widthPt * b.boxed.heightPt - a.boxed.widthPt * a.boxed.heightPt)
    );

  // Trial pack: everything on the sheet so far plus the candidate garment,
  // in that order. One bin = it fits whole.
  const trial = (entries: PreparedPiece[]) => {
    const packer = newPacker();
    for (const entry of entries) packer.add(rectOf(entry) as never);
    return packer;
  };

  let current: PreparedPiece[] = [];
  let currentPacker: any = null;
  const flush = () => {
    if (currentPacker) {
      const sheet = binToSheet(currentPacker.bins[0]);
      if (sheet) sheets.push(sheet);
    }
    current = [];
    currentPacker = null;
  };

  for (const garment of garments) {
    const attempt = trial([...current, ...garment]);
    if (attempt.bins.length === 1) {
      current = [...current, ...garment];
      currentPacker = attempt;
      continue;
    }
    if (current.length) {
      flush();
      const alone = trial(garment);
      if (alone.bins.length === 1) {
        current = [...garment];
        currentPacker = alone;
        continue;
      }
      // fall through: the garment alone overflows a sheet — split it
      for (const bin of alone.bins) {
        const sheet = binToSheet(bin);
        if (sheet) sheets.push(sheet);
      }
      continue;
    }
    // nothing on the sheet yet and the garment still overflows: split it
    for (const bin of attempt.bins) {
      const sheet = binToSheet(bin);
      if (sheet) sheets.push(sheet);
    }
  }
  flush();

  return { sheets, oversized, paperWidthPt, paperLengthPt, paddingPt };
}

export interface SheetBuildContext {
  paperWidthPt: number;
  paperLengthPt: number;
  /** contentKey -> namespaced isolated piece artwork (per row+panel). */
  contents: Map<string, string>;
  /** Root attrs recovered from a composite (namespace declarations). */
  rootAttrs: string;
  /** Laser-outline configuration (from factory settings). Defaults to the
   *  fixed cut outline only when omitted. */
  outlineSpec?: OutlineSpec;
  /** piece id -> searching-outline offset path (computeSearchOutlines). */
  searchOutlines?: Map<string, string>;
  /** Per-placement clip silhouette in sheet space (computeSheetClipPaths). */
  clipPaths?: Array<string | null>;
  /** Font to outline the size indicators with (glyph paths clip into the PowerClip). */
  labelFont?: GlyphFont | null;
  /**
   * piece.id -> label markup laid along the piece's inset silhouette
   * (computeEdgeLabels). Preferred over the straight centred label whenever
   * present, so the line follows a diamond's slanted edge or a collar's band
   * instead of poking out of the fabric at the lowest point.
   */
  edgeLabels?: Map<string, string>;
}

/** The one label line printed on a piece: size, then the optional extras. */
export function pieceLabelLine(piece: Pick<NestPieceInput, 'size' | 'labelExtra'>): string {
  const sizeText = (piece.size ?? '').trim();
  const extraText = (piece.labelExtra ?? '').trim();
  return [sizeText, extraText].filter(Boolean).join(' · ');
}

/**
 * Edge-following labels for every piece that has a cut silhouette. Pieces
 * without one (windowed fallbacks) keep the straight label. Keyed by piece
 * id, so quantity copies each get their own entry (same content).
 */
export async function computeEdgeLabels(
  pieces: Array<Pick<NestPieceInput, 'id' | 'size' | 'labelExtra' | 'outlineD'>>,
  font: GlyphFont | null | undefined
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (!font) return out;
  const byContent = new Map<string, string | null>();
  for (const piece of pieces) {
    if (!piece.outlineD) continue;
    const line = pieceLabelLine(piece);
    if (!line) continue;
    const memoKey = `${piece.outlineD}|${line}`;
    let markup = byContent.get(memoKey);
    if (markup === undefined) {
      const result = await edgeLabelForOutline(
        font,
        piece.outlineD,
        line,
        SIZE_LABEL_FONT_PT,
        SIZE_LABEL_OUTLINE_PT
      );
      markup = result?.markup ?? null;
      byContent.set(memoKey, markup);
    }
    if (markup) out.set(piece.id, markup);
  }
  return out;
}

function outlinePath(d: string, stroke: string, widthPt: number): string {
  // Round joins keep sharp corners from spiking past the reserved extent
  // (miter joins would).
  return (
    `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${round(widthPt)}"` +
    ` stroke-linejoin="round" stroke-linecap="round"/>`
  );
}

/** All outline strokes for one piece, drawn UNDER the artwork (whose fill
 *  covers each stroke's inward half):
 *  1. searching construction (optional): big black stroke out to
 *     offset+width/2, then a white mask clearing everything inside
 *     offset-width/2 — what survives outside the fabric is a line of `width`
 *     whose centerline sits `offset` out (a true offset curve without offset
 *     geometry);
 *  2. the cut band: a 2×width stroke whose visible half is exactly `width`
 *     going OUTWARDS from the panel edge. Drawn after the mask so the mask
 *     can't erase it. */
function outlineUnderMarkup(d: string, spec: OutlineSpec, searchD?: string | null): string {
  let markup = '';
  // Searching outline: a TRUE offset curve (computeSearchOutlines), stroked
  // only — nothing is painted between it and the panel. (The earlier
  // wide-black-stroke + white-mask trick left the gap filled white.)
  if (spec.searchEnabled && searchD) {
    markup += outlinePath(searchD, '#000000', mmToPt(spec.searchWidthMm));
  }
  markup += outlinePath(d, '#000000', 2 * mmToPt(spec.cutWidthMm));
  return markup;
}

/**
 * Offset silhouettes for the searching outline, keyed by PIECE ID and memoised
 * per distinct silhouette, in piece-local pt: the line's centerline runs
 * `search_outline_offset_mm` outside the panel edge. Keyed by piece, not by
 * contentKey: island pieces split from one panel share the artwork
 * (contentKey) but have different silhouettes — keyed by contentKey, islands
 * 2 and 3 were drawn with island 1's searching line. Empty when the
 * searching outline is off.
 */
export async function computeSearchOutlines(
  pieces: Array<Pick<NestPieceInput, 'id' | 'contentKey' | 'outlineD'>>,
  spec: OutlineSpec
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (!spec.searchEnabled) return out;
  const deltaPt = mmToPt(spec.searchOffsetMm);
  const byOutline = new Map<string, string | null>();
  for (const piece of pieces) {
    if (!piece.outlineD) continue;
    let d = byOutline.get(piece.outlineD);
    if (d === undefined) {
      d = await offsetPathData(piece.outlineD, deltaPt, 0.2);
      byOutline.set(piece.outlineD, d);
    }
    if (d) out.set(piece.id, d);
  }
  return out;
}

/**
 * One sheet as a physical-size SVG. Each distinct piece artwork (row+panel)
 * is defined once in <defs>; each placement maps its window into a local
 * w×h pt frame via a plain transform group with a rect clipPath — NOT a
 * nested <svg> viewport, which CorelDRAW's SVG import silently drops.
 * Copies of the same piece (quantity) reuse the same defs.
 */
export function buildSheetSvg(sheet: NestSheet, ctx: SheetBuildContext): string {
  const spec = ctx.outlineSpec ?? resolveOutlineSpec(null);

  // One clipPath per PLACEMENT, its silhouette already in sheet space
  // (computeSheetClipPaths) — CorelDRAW turns an untransformed clipped group
  // with a same-space clipPath into a PowerClip; anything else came in as a
  // plain group.
  const clipDefs = sheet.placements
    .map((_, i) => {
      const d = ctx.clipPaths?.[i];
      return d
        ? `<clipPath id="pc-${i + 1}" style="clip-rule:nonzero"><path d="${d}"/></clipPath>`
        : '';
    })
    .join('');

  const placements = sheet.placements
    .map((placement, i) => {
      const { piece } = placement;
      const win = piece.window;
      const { extent, transform } = placementFrame(placement, spec);
      const sx = win.width > 0 ? piece.widthPt / win.width : 1;
      const sy = win.height > 0 ? piece.heightPt / win.height : 1;

      // Piece artwork INLINED per placement (Corel handles inline curves in a
      // PowerClip; <use> instances did not survive as clipped content), ids
      // namespaced so quantity copies can't collide.
      const content = namespaceSvgIds(ctx.contents.get(piece.contentKey) ?? '', `c${i + 1}x`);
      const mapped =
        `<g transform="scale(${round(sx)} ${round(sy)}) translate(${round(-win.x)} ${round(-win.y)})">` +
        content +
        `</g>`;

      // Size indicator printed ON the piece in piece-local coordinates so it
      // rotates with the panel: black fill over a 0.5pt white outline, as
      // glyph OUTLINES when a font is available (Corel leaves <text> outside
      // the PowerClip). Anchored from the panel's exact geometric bottom
      // (piece.labelAnchor, window coords → local pt), else the window's
      // bottom-center.
      let labelX = piece.widthPt / 2;
      let labelY = piece.heightPt - SIZE_LABEL_MARGIN_PT;
      if (piece.labelAnchor && win.width > 0 && win.height > 0) {
        labelX = ((piece.labelAnchor.x - win.x) / win.width) * piece.widthPt;
        labelY = ((piece.labelAnchor.y - win.y) / win.height) * piece.heightPt;
      }
      // Size + extras (order/player/panel) as ONE line, baseline on the edge.
      const lineText = pieceLabelLine(piece);
      let label = '';
      const edgeLabel = ctx.edgeLabels?.get(piece.id);
      if (edgeLabel) {
        label = edgeLabel;
      } else if (lineText) {
        const outlined = ctx.labelFont
          ? sizeLabelPathMarkup(
              ctx.labelFont,
              labelX,
              labelY,
              lineText,
              SIZE_LABEL_FONT_PT,
              SIZE_LABEL_OUTLINE_PT
            )
          : null;
        label =
          outlined ??
          sizeLabelMarkup(labelX, labelY, lineText, SIZE_LABEL_FONT_PT, SIZE_LABEL_OUTLINE_PT);
      }

      // The packed slot includes the outline extent on every side; the piece
      // content shifts inward by that extent so the OUTLINE's outer edge is
      // what sits `padding` away from neighbors. The frame is applied twice —
      // once around the (unclipped) outlines, once inside the clipped group.
      const frame = (inner: string) =>
        `<g transform="${transform}">` +
        (extent > 0
          ? `<g transform="translate(${round(extent)}, ${round(extent)})">${inner}</g>`
          : inner) +
        '</g>';

      // Outlines render UNDER the artwork and OUTSIDE the clip: the artwork's
      // fill covers each stroke's inward half, so the visible bands sit
      // exactly outside the panel edge — a clip would cut the outer half off.
      const outlines = piece.outlineD
        ? frame(outlineUnderMarkup(piece.outlineD, spec, ctx.searchOutlines?.get(piece.id)))
        : '';

      const body = frame(`${mapped}${label}`);
      const clipped = ctx.clipPaths?.[i]
        ? `<g style="clip-path:url(#pc-${i + 1})"><g>${body}</g></g>`
        : body;

      return `<g data-piece="${escapeAttr(piece.id)}">${outlines}${clipped}</g>`;
    })
    .join('');

  const w = round(ctx.paperWidthPt);
  const l = round(ctx.paperLengthPt);
  return (
    `<svg ${ctx.rootAttrs} width="${w}pt" height="${l}pt" viewBox="0 0 ${w} ${l}">` +
    `<defs>${clipDefs}</defs>` +
    sheetFrameMarkup(ctx.paperWidthPt, ctx.paperLengthPt) +
    placements +
    '</svg>'
  );
}

/**
 * Per-placement clip silhouettes in SHEET space (piece-local pt mapped through
 * the placement frame, extent and rotation included). Fallback pieces (no
 * silhouette) clip to their window rectangle; null when neither applies.
 */
export async function computeSheetClipPaths(
  sheet: NestSheet,
  spec: OutlineSpec
): Promise<Array<string | null>> {
  return Promise.all(
    sheet.placements.map(async (placement) => {
      const { piece } = placement;
      const { matrix } = placementFrame(placement, spec);
      const local =
        piece.outlineD ??
        (piece.needsClip
          ? `M0 0h${round(piece.widthPt)}v${round(piece.heightPt)}h${-round(piece.widthPt)}z`
          : null);
      if (!local) return null;
      return unionPathData([{ d: local, matrix: IDENTITY, fillRule: 'nonzero' }], matrix);
    })
  );
}

/** Placement transform pieces shared by the sheet, cutlines and DXF builders. */
function placementFrame(
  placement: NestPlacement,
  spec: OutlineSpec
): { extent: number; transform: string; matrix: Mat } {
  const { piece, xPt, yPt, rotated } = placement;
  const extent = outlineExtentPt(piece, spec);
  const transform = rotated
    ? `translate(${round(xPt + piece.heightPt + 2 * extent)}, ${round(yPt)}) rotate(90)`
    : `translate(${round(xPt)}, ${round(yPt)})`;
  // Same mapping as a matrix (local piece pt → sheet pt), extent offset folded in.
  const place: Mat = rotated
    ? multiplyMat(
        [0, 1, -1, 0, xPt + piece.heightPt + 2 * extent, yPt],
        [1, 0, 0, 1, extent, extent]
      )
    : [1, 0, 0, 1, xPt + extent, yPt + extent];
  return { extent, transform, matrix: place };
}

export interface CutlinesSvgResult {
  svg: string;
  /** Placements without a silhouette (fallback pieces) — not in the file. */
  missing: number;
}

/**
 * Cut-lines-only companion file for one sheet: just the cut silhouettes as
 * hairline (0.25mm) black paths at their packed positions — no artwork, no
 * labels. What a laser/cutter RIP wants as the cut layer.
 */
export function buildCutlinesSvg(sheet: NestSheet, ctx: SheetBuildContext): string {
  return buildCutlinesSvgDetailed(sheet, ctx).svg;
}

export function buildCutlinesSvgDetailed(
  sheet: NestSheet,
  ctx: SheetBuildContext
): CutlinesSvgResult {
  const spec = ctx.outlineSpec ?? resolveOutlineSpec(null);
  let missing = 0;
  const paths = sheet.placements
    .map((placement) => {
      const { piece } = placement;
      if (!piece.outlineD) {
        missing++;
        return '';
      }
      const { transform, extent } = placementFrame(placement, spec);
      return (
        `<g transform="${transform}" data-piece="${escapeAttr(piece.id)}">` +
        `<g transform="translate(${round(extent)}, ${round(extent)})">` +
        `<path d="${piece.outlineD}" fill="none" stroke="#000000"` +
        ` stroke-width="${round(mmToPt(0.25))}" stroke-linejoin="round"/>` +
        `</g></g>`
      );
    })
    .join('');

  const w = round(ctx.paperWidthPt);
  const l = round(ctx.paperLengthPt);
  return {
    svg:
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}pt" height="${l}pt"` +
      ` viewBox="0 0 ${w} ${l}">` +
      sheetFrameMarkup(ctx.paperWidthPt, ctx.paperLengthPt) +
      `<g id="cutlines">${paths}</g></svg>`,
    missing,
  };
}

/**
 * DXF cut file for one sheet: every silhouette flattened to polylines
 * (0.2mm curve tolerance) on the CUT layer, in millimetres, y-up. Null when
 * the flattener is unavailable. Each distinct silhouette is flattened once
 * and re-placed per copy.
 */
export async function buildCutlinesDxf(
  sheet: NestSheet,
  ctx: SheetBuildContext
): Promise<string | null> {
  const spec = ctx.outlineSpec ?? resolveOutlineSpec(null);
  const toleranceP = mmToPt(0.2);
  const flatCache = new Map<string, Awaited<ReturnType<typeof flattenPathData>>>();
  const polylines: DxfPolyline[] = [];

  for (const placement of sheet.placements) {
    const { piece } = placement;
    if (!piece.outlineD) continue;
    // Memoised by silhouette, not contentKey (islands share artwork, not shape).
    let flat = flatCache.get(piece.outlineD);
    if (flat === undefined) {
      flat = await flattenPathData(piece.outlineD, [1, 0, 0, 1, 0, 0], toleranceP);
      flatCache.set(piece.outlineD, flat);
    }
    if (!flat) return null;
    const { matrix: m } = placementFrame(placement, spec);
    for (const poly of flat) {
      polylines.push({
        closed: poly.closed,
        points: poly.points.map(([x, y]) => {
          const sx = m[0] * x + m[2] * y + m[4];
          const sy = m[1] * x + m[3] * y + m[5];
          // pt → mm, y-up (DXF origin bottom-left).
          return [sx / PT_PER_MM, (ctx.paperLengthPt - sy) / PT_PER_MM] as [number, number];
        }),
      });
    }
  }
  return buildDxf(polylines);
}

export interface NestingSummaryInput {
  orderNo: string | number | null | undefined;
  productName: string;
  settings: NestSettings;
  paddingIn: number;
  result: NestResult;
  totalPieces: number;
  /** Panels unticked in setup (human labels), left out of every sheet. */
  omittedPanels?: string[];
  /** Sheets were packed per roster row (one sheet set per row). */
  perRow?: boolean;
}

export function buildNestingSummary(input: NestingSummaryInput): string {
  const { result, settings } = input;
  const spec = resolveOutlineSpec(settings);
  const lines: string[] = [
    `Nesting summary — order ${input.orderNo ?? 'n/a'} — ${input.productName}`,
    `Paper: ${settings.paper_width} x ${settings.paper_length} in`,
    `Allowed rotations: ${settings.allowed_rotations?.length ? settings.allowed_rotations.join(', ') : 'none'}` +
      (settings.allowed_rotations?.includes(180)
        ? ' (180 is orientation metadata only; it does not change packing)'
        : ''),
    `Piece padding: ${input.paddingIn} in (measured from the outermost cutting outline)`,
    `Sheet edge margin: ${SHEET_EDGE_MARGIN_MM / 10} cm (fixed)`,
    `Cut outline: ${spec.cutWidthMm} mm going outwards from the panel edge` +
      (spec.searchEnabled
        ? `; searching outline: ${spec.searchWidthMm} mm line at ${spec.searchOffsetMm} mm`
        : '; searching outline: off'),
    `Pieces: ${input.totalPieces} total, ${result.oversized.length} oversized (not placed)`,
    `Sheets: ${result.sheets.length}${input.perRow ? ' — one sheet set per roster row (rows are never mixed on a sheet)' : ''}`,
    ...(input.omittedPanels?.length
      ? [`Omitted panels (unticked in setup, not nested): ${input.omittedPanels.join(', ')}`]
      : []),
    '',
  ];

  result.sheets.forEach((sheet, i) => {
    lines.push(
      `${sheetFileStem(i, sheet)} — ${sheet.group ? `${sheet.group} — ` : ''}${sheet.garments?.length ? `garments ${garmentRange(sheet.garments)} — ` : ''}${sheet.placements.length} pieces — utilization ${(sheet.utilization * 100).toFixed(1)}%`
    );
  });

  if (result.oversized.length) {
    lines.push('', 'OVERSIZED (larger than the paper, not placed):');
    for (const piece of result.oversized) {
      lines.push(
        `- ${piece.id}: ${ptToInch(piece.widthPt).toFixed(1)} x ${ptToInch(piece.heightPt).toFixed(1)} in`
      );
    }
  }

  return lines.join('\n');
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
